import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendarAlt, FaCube } from 'react-icons/fa';
import { getSkillBySlug, pillarOf } from '../helpers/SkillsData';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import '../styles/Skills.css';
import '../styles/SkillDetail.css';

// three.js viewer loads in its own chunk, only for skills with a 3D showcase
const Hardware3DViewer = React.lazy(() => import('../components/Hardware3DViewer'));

function SkillDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language].skillsPage;
  const tp = translations[language].projectDisplay;
  const skill = useMemo(() => getSkillBySlug(slug), [slug]);
  const headingRef = useRef(null);

  // Back returns to the index with its search/category filters intact
  const backTo = `/skills${location.state?.fromSkills || ''}`;

  useEffect(() => {
    const prev = document.title;
    document.title = `${skill ? skill.name : t.detailNotFound} · Teoh Jia Kang`;
    return () => {
      document.title = prev;
    };
  }, [skill, t.detailNotFound]);

  // Move focus to the page heading so the route change is announced
  useEffect(() => {
    headingRef.current?.focus();
  }, [slug]);

  if (!skill) {
    return (
      <div className='skills-page'>
        <div className='skills-container'>
          <div className='skills-empty'>
            <h1 className='skill-detail-notfound' tabIndex={-1} ref={headingRef}>
              {t.detailNotFound}
            </h1>
            <Link className='skills-clear-btn skill-detail-back-btn' to={backTo}>
              <FaArrowLeft /> {t.detailBack}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='skills-page'>
      <motion.div
        className='skills-container'
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link className='skill-detail-back' to={backTo}>
          <FaArrowLeft /> {t.detailBack}
        </Link>

        <h1 className='page-title' tabIndex={-1} ref={headingRef}>
          {skill.name}
        </h1>
        <p className='skills-subtitle'>
          {t.pillars[pillarOf(skill.category)].name} · {t.categories[skill.category]} ·{' '}
          {skill.yearSpan}
        </p>
        <span className='skill-count'>
          {skill.projects.length}{' '}
          {skill.projects.length === 1 ? t.projectSingular : t.projectPlural}
        </span>

        {skill.showcases.length > 0 && (
          <section className='skill-detail-3d'>
            <h2 className='section-label'>
              <FaCube aria-hidden='true' /> {t.model3dTitle}
              {skill.showcases.length > 1 && ` (${skill.showcases.length})`}
            </h2>
            {skill.showcases.map((showcase) => (
              <div className='skill-showcase' key={showcase.captionKey}>
                {t.showcaseCaptions[showcase.captionKey] && (
                  <p className='skill-showcase-caption'>
                    {t.showcaseCaptions[showcase.captionKey]}
                  </p>
                )}
                <Suspense fallback={<div className='hw3d-fallback'>{tp.viewer3dLoading}</div>}>
                  {/* key remounts the viewer when navigating between showcase skills */}
                  <Hardware3DViewer
                    key={`${skill.slug}-${showcase.captionKey}`}
                    parts={showcase.parts}
                    defaultOpacity={showcase.defaultOpacity}
                    labels={{
                      loading: tp.viewer3dLoading,
                      hint: tp.viewer3dHint,
                      opacity: tp.viewer3dOpacity,
                      fallback: tp.viewer3dFallback,
                      // Caption in the label keeps multiple models on one page
                      // distinguishable for assistive tech
                      ariaLabel: [
                        `${t.model3dTitle} — ${skill.name}`,
                        t.showcaseCaptions[showcase.captionKey],
                      ]
                        .filter(Boolean)
                        .join(' · '),
                      parts: {
                        pcb: tp.viewer3dPartPcb,
                        body: tp.viewer3dPartBody,
                        cover: tp.viewer3dPartCover,
                        plates: tp.viewer3dPartPlates,
                        frame: tp.viewer3dPartFrame,
                        housing: tp.viewer3dPartHousing,
                      },
                    }}
                  />
                </Suspense>
              </div>
            ))}
          </section>
        )}

        <section className='skill-detail-evidence'>
          <h2 className='section-label'>{t.detailEvidence}</h2>
          <div className='skill-evidence-grid'>
            {skill.projects.map((p, index) => (
              <motion.div
                key={p.projectId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.3) }}
              >
                <Link className='skill-evidence-card glass' to={`/project/${p.projectId}`}>
                  {/* alt='' — the card's h3 already names the project for AT */}
                  <div className='skill-evidence-media'>
                    <img src={p.image} alt='' loading='lazy' decoding='async' />
                  </div>
                  <div className='skill-evidence-body'>
                    <h3>{p.name}</h3>
                    {p.tagline && <p className='skill-evidence-tagline'>{p.tagline}</p>}
                    <span className='skill-evidence-year'>
                      <FaCalendarAlt aria-hidden='true' /> {p.year}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>
    </div>
  );
}

export default SkillDetail;
